import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, ArrowRight } from "lucide-react";
import { fetchMySubscriptions, fetchSubscriptionUsage } from "../../redux/network/userApi";

const SubscriptionSuccess = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwtToken = localStorage.getItem("BQS_TOKEN");
  const subscriptionsState = useSelector((state) => state.user.subscriptions);
  const activeSubs = subscriptionsState.value || [];
  
  const [isSuccess, setIsSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const MAX_RETRIES = 20; // 20 attempts * 3s = 60 seconds total

  useEffect(() => {
    // If we already detected an active sub or timed out, don't keep polling
    if (isSuccess || hasTimedOut) return;

    const intervalId = setInterval(() => {
      setRetryCount((prev) => {
        if (prev >= MAX_RETRIES) {
          setHasTimedOut(true);
          return prev;
        }
        dispatch(fetchMySubscriptions(jwtToken, "active", true));
        return prev + 1;
      });
    }, 3000);

    return () => clearInterval(intervalId);
  }, [isSuccess, hasTimedOut, dispatch, jwtToken]);

  useEffect(() => {
    // Check if there is an active paid plan that is NOT cancelled
    const hasPaidActivePlan = activeSubs.some((sub) => {
        const sStat = String(sub.status || sub.state || '').trim().toLowerCase();
        const isPending = !!sub.pendingCancelAt || sStat.includes('cancel') || sStat === 'non_renewing';
        return sStat === "active" && !isPending && sub.plan?.price > 0;
    });
    
    if (hasPaidActivePlan) {
      dispatch(fetchSubscriptionUsage(jwtToken, "smartInsights"));
      setIsSuccess(true);
    }
  }, [activeSubs, jwtToken, dispatch]);

  useEffect(() => {
    if (!isSuccess) return;

    const intervalId = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          navigate("/game-posts/game-videos");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isSuccess, navigate]);

  const handleRedirect = () => {
    navigate("/game-posts/game-videos");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white max-w-md w-full rounded-[2rem] p-8 shadow-xl shadow-slate-200/50 flex flex-col items-center text-center border border-slate-100">
        {hasTimedOut ? (
          <>
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mb-6 border-4 border-amber-100">
              <Loader2 className="w-10 h-10 text-amber-500 opacity-50" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-3 uppercase tracking-tight">
              Taking longer than expected
            </h1>
            <p className="text-slate-500 font-medium mb-6 px-4">
              Your payment was received, but the account activation is taking a moment. You can refresh this page or check back in a few minutes.
            </p>
            <div className="flex flex-col gap-3 w-full">
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-[#247E45] text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-emerald-200/50 hover:bg-[#1b6136] transition-all"
                >
                  Refresh Page
                </button>
                <p className="text-xs text-slate-400 font-medium">
                  If your plan isn't active after 10 minutes, please contact support.
                </p>
            </div>
          </>
        ) : !isSuccess ? (
          <>
            <div className="w-20 h-20 bg-[#247E45]/10 rounded-full flex items-center justify-center mb-6">
              <Loader2 className="w-10 h-10 text-[#247E45] animate-spin" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-3 uppercase tracking-tight">
              Verifying Payment
            </h1>
            <p className="text-slate-500 font-medium">
              Please wait while we confirm your subscription. Do not close this window.
            </p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-[#247E45]/20 rounded-full flex items-center justify-center mb-6 ring-4 ring-[#247E45]/10 animate-bounce">
              <CheckCircle className="w-10 h-10 text-[#247E45]" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-3 uppercase tracking-tight">
              Payment Successful!
            </h1>
            <p className="text-slate-600 font-medium mb-6">
              Your new subscription plan has been fully activated.
            </p>
            
            <div className="bg-[#247E45]/5 border border-[#247E45]/10 rounded-2xl p-6 w-full flex flex-col items-center gap-4 transition-all">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#247E45]/10 text-[#247E45] font-black text-lg border border-[#247E45]/20 animate-pulse">
                {countdown}
              </div>
              <p className="text-sm text-slate-600 font-medium">
                Redirecting to your <span className="font-bold text-slate-900">Videos</span> page in <span className="font-black text-[#247E45]">{countdown}s</span>...
              </p>
              
              <button 
                onClick={handleRedirect}
                className="w-full flex items-center justify-center gap-2 bg-[#247E45] text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-[#247E45]/25 hover:bg-[#1b6136] transition-all transform hover:-translate-y-0.5 active:translate-y-0"
              >
                Go to My Videos Now
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
